import { useEffect, useState } from 'react'

import { StyleView } from '../../../../core/models/entities/StyleView'
import { ViewSelector } from '../../style-edit/subcomponents/style-edit-form/ViewSelector'

interface StyleViewerProps { views: StyleView[] }

// The view images are full product photos (~350-530 KB each). Swapping the
// strip's img srcs directly painted each view as it finished downloading, so
// for a few seconds the strip showed a mix of the previous and the new model.
// Instead: keep showing the previous model dimmed, preload ALL new views, and
// swap the whole set at once (with a safety timeout so a dead image can't
// block the swap forever).
export function StyleViewer({ views }: StyleViewerProps) {
  const [displayViews, setDisplayViews] = useState<StyleView[]>(views)
  const [currentView, setCurrentView] = useState<StyleView>(views[0])
  const [isSwitching, setIsSwitching] = useState(false)

  useEffect(() => {
    let cancelled = false
    setIsSwitching(true)

    const preloads = views.map(v => new Promise<void>(resolve => {
      if (!v.imageUrl) return resolve()
      const img = new Image()
      img.onload = () => resolve()
      img.onerror = () => resolve()
      img.src = v.imageUrl
      if (img.complete) resolve()
    }))
    const safetyTimeout = new Promise<void>(resolve => setTimeout(resolve, 4000))

    Promise.race([Promise.all(preloads), safetyTimeout]).then(() => {
      if (cancelled) return
      setDisplayViews(views)
      setCurrentView(views[0])
      setIsSwitching(false)
    })

    return () => { cancelled = true }
  }, [views])

  const dimWhileSwitching =
    'transition-opacity duration-150 ' + (isSwitching ? 'opacity-40' : 'opacity-100')

  return <>
    <div className='flex flex-1 w-full justify-center items-center py-8'>
      <div
        className={`w-full h-full p-5 bg-contain bg-no-repeat bg-center ${dimWhileSwitching}`}
        style={{ backgroundImage: `url(${currentView?.imageUrl})` }}
      />
    </div>
    <div className={dimWhileSwitching}>
      <ViewSelector
        views={displayViews}
        onSelectView={x => setCurrentView(x)}
        currentView={currentView}
      />
    </div>
  </>
}
